import { GeoCoordinates } from "@type/geolocation.types"
import { Map, useApiIsLoaded, useMap, useMapsLibrary } from "@vis.gl/react-google-maps"
import { Segmented } from "antd-v5"
import { useEffect, useMemo, useState } from "react"
import { HeatMapOutlined, PushpinOutlined } from "@ant-design/icons"
import { SignedUrlForImage } from "./SignedUrl"
import CustomAdvancedMarker from "./CustomAdvancedMarker"
import { DATE_FORMATS, defaultGeocodeData, ShiftStatus } from "config/constants"
import { momentZ } from "config/moment"
import { cn } from "config/utils"
import Icon from "./Icon"

export enum MapConfig {
	PIN = "PIN",
	TRACK = "TRACK",
	HEATMAP = "HEATMAP",
}

interface ShiftVisualizationProps {
	selectedCall: ShiftStatus
	markers: {
		_id: string
		name?: string
		color: string
		status: ShiftStatus
		eventTime: string
		dueTime: string
		position: GeoCoordinates
		isSelected?: boolean
		employee: string
		images: string[]
	}[]
	heatmap: {
		position: GeoCoordinates
	}[]
	polyLines: {
		status: ShiftStatus
		dueTime: string
		lineInfo: {
			_id: string
			images: string[]
			position: GeoCoordinates
		}[]
	}[]
	selectedMarker: { [dueTime: string]: string } | null
	setSelectedMarker: (dueTime: string, _id: string) => void
	legends?: { title: string; color: string }[]
	onMapConfigChange?: (value: MapConfig) => void
}

const ShiftActivityMapVisualization: React.FC<ShiftVisualizationProps> = ({
	markers,
	selectedCall,
	selectedMarker,
	setSelectedMarker,
	legends,
	heatmap,
	polyLines,
	onMapConfigChange,
}) => {
	const [selectedMapConfig, setSelectedMapConfig] = useState<MapConfig>(MapConfig.PIN)
	const [disableAutoFit, setDisableAutoFit] = useState(false)
	const map = useMap()

	useEffect(() => {
		setSelectedMapConfig(MapConfig.PIN)
	}, [selectedCall])

	const boundPoints = useMemo(() => {
		if (
			selectedCall !== ShiftStatus.QR &&
			(selectedMapConfig === MapConfig.PIN || selectedMapConfig === MapConfig.TRACK)
		)
			return markers
				.filter(({ position: { lat, lng } }) => lat !== 0 && lng !== 0)
				.map(({ position }) => position)

		if (
			selectedCall === ShiftStatus.QR &&
			(selectedMapConfig === MapConfig.PIN || selectedMapConfig === MapConfig.TRACK)
		) {
			return polyLines.flatMap(polyLine => {
				return polyLine.lineInfo.map(({ position }) => position)
			})
		} else
			return heatmap
				.filter(({ position: { lat, lng } }) => lat !== 0 && lng !== 0)
				.map(({ position }) => position)
	}, [selectedCall, markers, heatmap, polyLines])

	return (
		<div className="box-border h-full w-full">
			<Map
				mapId="9755c364cfa2df42a1c605dc"
				className="relative h-full w-full"
				defaultCenter={defaultGeocodeData.results[0].geometry.location}
				defaultZoom={3}
				disableDefaultUI={true}
			>
				<Segmented
					hidden={selectedCall === ShiftStatus.QR ? false : true}
					rootClassName="absolute shadow-sm top-3 left-3"
					value={selectedMapConfig}
					onChange={val => {
						setSelectedMapConfig(val as MapConfig)
						onMapConfigChange?.(val as MapConfig)
					}}
					options={[
						{
							label: (
								<div className="flex items-center justify-center space-x-1">
									<PushpinOutlined className="text-xl" />
									<span>Pin</span>
								</div>
							),
							value: MapConfig.PIN,
							className: "w-24",
						},
						{
							label: (
								<div className="flex items-center justify-center space-x-1">
									<Icon
										name="walk"
										className="text-xl"
										color={selectedMapConfig === MapConfig.TRACK ? "purple" : "antGray"}
									/>
									<span>Track</span>
								</div>
							),
							value: MapConfig.TRACK,
							className: "w-24",
						},
						{
							label: (
								<div className="flex items-center justify-center space-x-1">
									<HeatMapOutlined className="text-xl" />
									<span>Heatmap</span>
								</div>
							),
							value: MapConfig.HEATMAP,
							className: "w-24",
						},
					]}
				/>

				<div className="absolute  bottom-0 flex w-[865px] flex-wrap space-x-5 bg-gray-100 ">
					{selectedMapConfig !== MapConfig.HEATMAP &&
						legends?.map(({ color, title }) => {
							return (
								<div className="flex items-center justify-center space-x-1" key={color}>
									<div style={{ background: color }} className={cn(`h-2.5 w-2.5 rounded-full`)} />
									<div>{title}</div>
								</div>
							)
						})}
				</div>

				<FitBounds points={boundPoints} disabled={disableAutoFit} />

				{selectedMapConfig === MapConfig.PIN &&
					markers.map(({ _id, position, images = [], dueTime, status, eventTime, employee, name, color }) => {
						return (
							<>
								<SignedUrlForImage path={images} key={`loadedImage_${_id}}`}>
									{(images = [], loading) => {
										return (
											<CustomAdvancedMarker
												loading={loading}
												pinColor={color}
												position={position}
												onMarkerClick={(markerPosition, clickState) => {
													if (map) {
														const bounds = new window.google.maps.LatLngBounds()
														bounds.extend(markerPosition)
														map.fitBounds(bounds)

														setDisableAutoFit(!clickState)
													}
													setSelectedMarker(dueTime, _id)
												}}
												isMarkerSelected={() => selectedMarker?.[dueTime] === _id}
												markerEventDetails={{
													name,
													employee,
													images,
													status,
													dueTime: momentZ(dueTime).format(
														DATE_FORMATS.SHIFT_EXPECTED_TIME_FORMAT,
													),
													eventTime: momentZ(eventTime).format(
														DATE_FORMATS.SHIFT_EXPECTED_TIME_FORMAT,
													),
												}}
											/>
										)
									}}
								</SignedUrlForImage>
							</>
						)
					})}

				{selectedMapConfig === MapConfig.TRACK &&
					polyLines.map(({ dueTime, lineInfo }) => {
						return (
							<div key={dueTime.valueOf()}>
								<PolyLineOverlay lineInfo={lineInfo} />
							</div>
						)
					})}

				{selectedMapConfig === MapConfig.HEATMAP && <HeatmapOverlay heatMapData={heatmap} />}
			</Map>
		</div>
	)
}

const HeatmapOverlay: React.FC<{ heatMapData: { position: GeoCoordinates }[] }> = ({ heatMapData }) => {
	const map = useMap()
	const loaded = useApiIsLoaded()

	const visualization = useMapsLibrary("visualization")

	useEffect(() => {
		if (!loaded || !map || !visualization) return

		const heatmap: google.maps.visualization.HeatmapLayer = new visualization.HeatmapLayer({
			data: heatMapData.map(({ position: { lat, lng } }) => new google.maps.LatLng(lat, lng)),
		})

		heatmap.setMap(map)

		return () => heatmap.setMap(null)
	}, [map, heatMapData, visualization])

	return null
}

interface PolyLineProps {
	lineInfo: {
		_id: string
		images: string[]
		position: GeoCoordinates
	}[]
}

const PolyLineOverlay: React.FC<PolyLineProps> = ({ lineInfo }) => {
	const map = useMap()
	const loaded = useApiIsLoaded()

	useEffect(() => {
		if (!map || !loaded) return

		const polyline = new google.maps.Polyline({
			path: lineInfo.map(({ position: { lat, lng } }) => new google.maps.LatLng(lat, lng)),
			strokeColor: "#4285F4",
			strokeOpacity: 0.7,
			strokeWeight: 4,
			icons: [
				{
					icon: {
						path: google.maps.SymbolPath.FORWARD_OPEN_ARROW,
						scale: 2,
						strokeColor: "#000000",
						strokeWeight: 3,
					},
					offset: "100%",
					repeat: "250px",
				},
			],
		})

		polyline.setMap(map)

		return () => polyline.setMap(null) // cleanup
	}, [map, lineInfo])

	return null
}

interface FitBoundsInterface {
	points: GeoCoordinates[]
	disabled?: boolean
}

const FitBounds: React.FC<FitBoundsInterface> = ({ points, disabled }) => {
	const map = useMap()
	const loaded = useApiIsLoaded()

	useEffect(() => {
		if (disabled || !map || !loaded || !points.length) return

		const bounds = new google.maps.LatLngBounds()
		points.forEach(({ lat, lng }) => bounds.extend(new google.maps.LatLng(lat, lng)))

		map.fitBounds(bounds) // padding in px
	}, [map, loaded, points])

	return null
}

export default ShiftActivityMapVisualization
